/**
 * Publisher base class for objects that emit events.
 * Used by both Actions and Stores to notify listeners of changes.
 *
 * @module reflux/publisher
 */

import EventEmitter from 'eventemitter3';
import { trackSub, untrackSub } from './monitoring.js';

/**
 * @typedef {Object} Publisher
 * @property {EventEmitter} emitter - The underlying event emitter
 * @property {string} eventLabel - The event name used for emissions
 * @property {Function} preEmit - Hook invoked before emitting
 * @property {Function} shouldEmit - Gate to determine if event should be emitted
 * @property {Function} listen - Subscribe to events from this publisher
 * @property {Function} trigger - Trigger event to all listeners synchronously
 * @property {Function} triggerAsync - Trigger event asynchronously on next tick
 */

/**
 * Utility to check if value is arguments object.
 *
 * @param {*} value - Value to check
 * @returns {boolean} True if value is arguments object
 */
const isArguments = (value) => {
  return typeof value === 'object' &&
         value !== null &&
         'callee' in value &&
         typeof value.length === 'number';
};

/**
 * Creates an object with publishing capabilities.
 * Used as the foundation for Actions and Stores.
 *
 * @param {string} [eventLabel='event'] - The event name to use for emissions
 * @param {string} [publisherName] - Display name for memory tracking (defaults to eventLabel)
 * @param {string} [publisherType] - Type for memory tracking: 'action', 'store', or null
 * @returns {Publisher} Publisher instance with listen, trigger, and triggerAsync methods
 *
 * @example
 * const publisher = createPublisher('change');
 * const unlisten = publisher.listen(() => console.log('triggered'));
 * publisher.trigger('data');
 * unlisten();
 *
 * @example
 * // With custom name and type for memory tracking
 * const publisher = createPublisher('update', 'UserStore', 'store');
 */
export const createPublisher = (eventLabel = 'event', publisherName = null, publisherType = null) => {
  const emitter = new EventEmitter();
  // Use publisherName for tracking, fallback to eventLabel
  const trackingName = publisherName || eventLabel;

  const publisher = {
    emitter,
    eventLabel,

    /**
     * Hook invoked before emitting. Can transform arguments.
     * Override this in store/action definitions to modify data before emission.
     * When overriding, the function receives trigger arguments as parameters.
     *
     * @returns {*} Transformed arguments or undefined to keep original
     *
     * @example
     * publisher.preEmit = function(...args) {
     *   return args.map(x => x.toUpperCase());
     * };
     */
    preEmit() {
      // Default: no transformation
      return undefined;
    },

    /**
     * Hook to determine if event should be emitted.
     * Override this in store/action definitions to conditionally emit.
     * When overriding, the function receives trigger arguments as parameters.
     *
     * @returns {boolean} True if event should be emitted
     *
     * @example
     * publisher.shouldEmit = function(data) {
     *   return data !== null;
     * };
     */
    shouldEmit() {
      return true;
    },

    /**
     * Subscribe to events from this publisher.
     * Returns an unlisten function to cancel the subscription.
     *
     * IMPORTANT: Uses abort flag pattern to prevent race conditions.
     *
     * Race condition scenario without abort flag:
     * 1. Event is triggered, starts iterating through listeners
     * 2. Callback A is called
     * 3. Callback A calls unlisten() for callback B
     * 4. Callback B is removed from listener array WHILE we're iterating
     * 5. Iterator gets confused, may skip listeners or call removed ones
     *
     * Solution: Set abort flag BEFORE removing listener, check flag in wrapper.
     * This ensures already-queued events can check the flag and skip execution.
     *
     * @param {Function} callback - Function to call when event is triggered
     * @param {Object} [bindContext] - Context to bind callback to (defaults to publisher)
     * @returns {Function} Unlisten function to cancel subscription
     *
     * @example
     * const unlisten = action.listen((data) => {
     *   console.log('Action triggered:', data);
     * });
     * // Later...
     * unlisten();
     *
     * @example
     * // With custom context
     * const unlisten = store.listen(function(state) {
     *   this.setState(state);
     * }, component);
     */
    listen(callback, bindContext) {
      bindContext = bindContext || this;
      let aborted = false;

      // Track subscription for memory monitoring (if enabled)
      const trackingId = trackSub(trackingName, publisherType);

      // Wrapper that unwraps args array and respects abort flag
      // This matches Reflux behavior where args are wrapped in array for emit
      /** @param {Array} args */
      const eventHandler = (args) => {
        // Check abort flag - prevents execution if unlisten was called
        // during this event emission cycle
        if (aborted) {
          return;
        }
        callback.apply(bindContext, args);
      };

      emitter.addListener(eventLabel, eventHandler);

      // Return unlisten function
      // Sets abort flag FIRST, then removes listener
      // This order is critical for race condition prevention
      return () => {
        aborted = true;  // Prevents future executions of already-queued events
        untrackSub(trackingId);  // Remove from memory tracking
        emitter.removeListener(eventLabel, eventHandler);
      };
    },

    /**
     * Trigger event to all listeners synchronously.
     * Arguments are passed through preEmit hook and shouldEmit gate.
     *
     * @param {...*} args - Arguments to pass to listeners
     *
     * @example
     * action.trigger('user123', { name: 'John' });
     *
     * @example
     * // Multiple arguments are preserved
     * store.trigger(newState, previousState);
     */
    trigger(...args) {
      // Apply preEmit hook
      const pre = this.preEmit.apply(this, args);

      // Transform args if preEmit returned something
      if (pre !== undefined) {
        // Handle arguments object (convert to array)
        args = isArguments(pre) ? Array.from(pre) : [].concat(pre);
      }

      // Check shouldEmit gate
      if (this.shouldEmit.apply(this, args)) {
        // Emit with args wrapped in array
        // Listeners receive wrapped array and unwrap via apply()
        emitter.emit(eventLabel, args);
      }
    },

    /**
     * Trigger event asynchronously, before the next macrotask.
     * Useful for breaking synchronous call chains and preventing stack overflows.
     *
     * NOTE: Uses queueMicrotask() instead of the original Reflux v0.4.1
     * setTimeout(0). Stores synchronize by re-dispatching actions from their
     * handlers, so a single user action hops across several dispatches. With
     * setTimeout(0) each hop landed in a separate macrotask, letting React 18
     * commit a render between hops with mixed old/new state (app-wide
     * new -> old -> new multi-renders). Microtask delivery drains the entire
     * cascade before React's next scheduled commit — one commit per action —
     * while still preserving run-to-completion (handlers never run
     * synchronously inside the dispatching call, so no reentrancy).
     *
     * @param {...*} args - Arguments to pass to listeners
     *
     * @example
     * action.triggerAsync('data'); // Fires after the current task completes
     *
     * @example
     * // Use in recursive scenarios to prevent stack overflow
     * const recursiveAction = createPublisher('recursive');
     * recursiveAction.listen(() => {
     *   if (shouldContinue) {
     *     recursiveAction.triggerAsync(); // Async prevents stack overflow
     *   }
     * });
     */
    triggerAsync(...args) {
      queueMicrotask(() => {
        this.trigger(...args);
      });
    },
  };

  return publisher;
};
